/**
 * Every version this borrower-period has had.
 *
 * v1 is a list, not a diff: what a reviewer needs first is whether the numbers
 * on screen are the ones that were locked, and when a regeneration replaced
 * what they last read. Comparing two versions cell by cell is a later ticket.
 */

import React from "react";

import StatusPill from "../StatusPill";
import type { MonitoringRunVersion } from "../../types/monitoring";
import { formatEpochMs } from "./runFormat";

export interface RunVersionsListProps {
	versions: MonitoringRunVersion[];
	loading: boolean;
	error: Error | undefined;
	/** The version currently on screen. */
	currentVersion: number;
}

const STATUS_TONE: Record<
	MonitoringRunVersion["status"],
	"grey" | "green" | "teal"
> = { draft: "grey", reviewed: "green", locked: "teal" };

const RunVersionsList: React.FC<RunVersionsListProps> = ({
	versions,
	loading,
	error,
	currentVersion,
}) => {
	if (loading) {
		return (
			<div className="h-24 animate-pulse rounded-lg border border-gray-200 bg-white" />
		);
	}
	if (error) {
		return (
			<p role="alert" className="text-sm text-red-600">
				Could not load this run's history — {error.message}
			</p>
		);
	}
	if (versions.length === 0) {
		return (
			<p className="rounded-lg border border-gray-200 bg-white px-4 py-6 text-sm text-text-muted">
				No earlier versions.
			</p>
		);
	}

	return (
		<ul className="flex flex-col gap-2">
			{[...versions]
				.sort((a, b) => b.version - a.version)
				.map((version) => (
					<li
						key={`${version.public_id}-${version.version}`}
						data-testid={`run-version-${version.version}`}
						className="flex flex-wrap items-center gap-3 rounded-lg border border-gray-200 bg-white p-3"
					>
						<span className="text-base font-medium text-text-base">
							v{version.version}
						</span>
						<StatusPill
							tone={STATUS_TONE[version.status]}
							label={version.status}
						/>
						{version.version === currentVersion && (
							<span className="text-sm text-text-muted">on screen</span>
						)}
						<span className="text-sm text-text-muted">
							Generated {formatEpochMs(version.generated_at)}
						</span>
						{version.locked_at && (
							<span className="text-sm text-text-muted">
								Locked {formatEpochMs(version.locked_at)}
							</span>
						)}
						{version.generation_error && (
							<span className="text-sm text-red-600">
								{version.generation_error}
							</span>
						)}
					</li>
				))}
		</ul>
	);
};

export default RunVersionsList;
